import React, { useState, useEffect, useRef } from 'react';
import {
  GitCompare,
  Clock,
  Zap,
  ArrowRight,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  TrendingDown,
  Users,
  Activity,
  Shield
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface ProcessFlowComparisonProps {
  opportunityId: number;
  projectId: number;
  opportunityTitle: string;
}

interface ProcessFlowData {
  before: {
    steps: ProcessFlowStep[];
    totalDuration: number;
    totalManualEffort: number;
    totalHandoffs: number;
    bottlenecks: string[];
  };
  after: {
    steps: ProcessFlowStep[];
    totalDuration: number;
    totalManualEffort: number;
    totalHandoffs: number;
    automatedSteps: string[];
  };
  transformation: {
    timeSaved: number;
    timeSavedPercentage: number;
    effortReduction: number;
    effortReductionPercentage: number;
    stepsEliminated: number;
    stepsAutomated: number;
    handoffsReduced: number;
  };
  complianceControls?: Array<{
    type: string;
    controls: Array<{
      requirement?: string;
      owner?: string;
      control?: string;
      implementation?: string;
    }>;
  }>;
}

interface ProcessFlowStep {
  id: string;
  name: string;
  description: string;
  duration: number; // in minutes
  manualEffort: number; // in minutes
  owner: string;
  department: string;
  isManual: boolean;
  isBottleneck?: boolean;
  isEliminated?: boolean;
  isAutomated?: boolean;
  isReduced?: boolean;
  automationDetails?: string;
  position: { x: number; y: number };
  connections: string[]; // IDs of next steps
}

const ProcessFlowComparison: React.FC<ProcessFlowComparisonProps> = ({
  opportunityId,
  projectId,
  opportunityTitle
}) => {
  const [view, setView] = useState<'before' | 'after' | 'comparison'>('comparison');
  const [loading, setLoading] = useState(true);
  const [flowData, setFlowData] = useState<ProcessFlowData | null>(null);
  const [exporting, setExporting] = useState(false);
  const diagramRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadProcessFlowData();
  }, [opportunityId, projectId]);

  const loadProcessFlowData = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/process-optimization/${projectId}/opportunities/${opportunityId}/process-flow`
      );
      setFlowData(response.data);
    } catch (error) {
      console.error('Failed to load process flow data:', error);
      toast.error('Failed to load process flow visualization');
    } finally {
      setLoading(false);
    }
  };

  const exportAsPNG = async () => {
    if (!diagramRef.current) return;

    try {
      setExporting(true);
      const canvas = await html2canvas(diagramRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });

      const link = document.createElement('a');
      link.download = `process-flow-${opportunityId}-${view}.png`;
      link.href = canvas.toDataURL();
      link.click();

      toast.success('Diagram exported as PNG');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export diagram');
    } finally {
      setExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!diagramRef.current) return;

    try {
      setExporting(true);
      const canvas = await html2canvas(diagramRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`process-flow-${opportunityId}-${view}.pdf`);

      toast.success('Diagram exported as PDF');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export diagram');
    } finally {
      setExporting(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const renderStepCard = (step: ProcessFlowStep, isAfter: boolean = false) => {
    let borderColor = 'border-gray-300';
    let bgColor = 'bg-white';
    let badgeColor = 'bg-gray-100 text-gray-700';
    let badgeText = 'Manual';

    if (step.isEliminated) {
      borderColor = 'border-red-300';
      bgColor = 'bg-red-50';
      badgeColor = 'bg-red-100 text-red-700';
      badgeText = 'Eliminated';
    } else if (step.isAutomated) {
      borderColor = 'border-green-500';
      bgColor = 'bg-green-50';
      badgeColor = 'bg-green-100 text-green-700';
      badgeText = 'Automated';
    } else if (step.isReduced) {
      borderColor = 'border-blue-500';
      bgColor = 'bg-blue-50';
      badgeColor = 'bg-blue-100 text-blue-700';
      badgeText = 'Optimized';
    } else if (step.isBottleneck) {
      borderColor = 'border-orange-500';
      bgColor = 'bg-orange-50';
    }

    return (
      <div
        key={step.id}
        className={`relative ${bgColor} border-2 ${borderColor} rounded-lg p-3 shadow-sm transition-all hover:shadow-md ${
          step.isEliminated ? 'opacity-60' : ''
        }`}
        style={{ minWidth: '200px', maxWidth: '250px' }}
      >
        {/* Badge */}
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold px-2 py-1 rounded ${badgeColor}`}>
            {badgeText}
          </span>
          {step.isBottleneck && !step.isEliminated && (
            <div title="Bottleneck">
              <AlertCircle className="w-4 h-4 text-orange-600" />
            </div>
          )}
          {step.isEliminated && (
            <div title="Eliminated">
              <XCircle className="w-4 h-4 text-red-600" />
            </div>
          )}
          {step.isAutomated && (
            <div title="Automated">
              <Zap className="w-4 h-4 text-green-600" />
            </div>
          )}
        </div>

        {/* Step Name */}
        <h4 className={`font-semibold text-sm mb-1 ${step.isEliminated ? 'line-through text-gray-500' : 'text-gray-900'}`}>
          {step.name}
        </h4>

        {/* Description */}
        {step.description && (
          <p className="text-xs text-gray-600 mb-2">{step.description}</p>
        )}

        {/* Metrics */}
        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-gray-600">
              <Clock className="w-3 h-3" />
              Duration:
            </span>
            <span className={`font-semibold ${step.isReduced ? 'text-blue-600' : 'text-gray-900'}`}>
              {formatDuration(step.duration)}
            </span>
          </div>

          {step.manualEffort > 0 && (
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-gray-600">
                <Activity className="w-3 h-3" />
                Effort:
              </span>
              <span className="font-semibold text-gray-900">
                {formatDuration(step.manualEffort)}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-gray-600">
              <Users className="w-3 h-3" />
              Owner:
            </span>
            <span className="font-semibold text-gray-900 text-xs truncate ml-1">
              {step.owner}
            </span>
          </div>
        </div>

        {/* Automation Details */}
        {step.automationDetails && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-xs text-green-700 italic">{step.automationDetails}</p>
          </div>
        )}
      </div>
    );
  };

  const renderProcessFlow = (steps: ProcessFlowStep[], isAfter: boolean = false) => {
    return (
      <div className="relative bg-gray-50 rounded-lg p-6 min-h-[500px]">
        <div className="flex flex-wrap gap-4 items-start">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              {renderStepCard(step, isAfter)}
              {index < steps.length - 1 && (
                <ArrowRight className="w-6 h-6 text-gray-400 mx-2 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTimelineComparison = () => {
    if (!flowData) return null;

    const beforeDuration = flowData.before.totalDuration;
    const afterDuration = flowData.after.totalDuration;
    const maxDuration = Math.max(beforeDuration, afterDuration);

    return (
      <div className="bg-white rounded-lg border-2 border-purple-200 p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-purple-600" />
          Timeline Compression Visualization
        </h3>

        <div className="space-y-6">
          {/* Before Timeline */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">Before Automation</span>
              <span className="text-sm font-bold text-gray-900">{formatDuration(beforeDuration)}</span>
            </div>
            <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute h-full bg-gradient-to-r from-red-400 to-orange-400 rounded-full transition-all"
                style={{ width: '100%' }}
              >
                <div className="flex items-center justify-center h-full text-white text-xs font-semibold">
                  {flowData.before.steps.length} steps
                </div>
              </div>
            </div>
          </div>

          {/* After Timeline */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">After Automation</span>
              <span className="text-sm font-bold text-green-600">{formatDuration(afterDuration)}</span>
            </div>
            <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all"
                style={{ width: `${(afterDuration / maxDuration) * 100}%` }}
              >
                <div className="flex items-center justify-center h-full text-white text-xs font-semibold">
                  {flowData.after.steps.filter(s => !s.isEliminated).length} steps
                </div>
              </div>
            </div>
          </div>

          {/* Savings Indicator */}
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {flowData.transformation.timeSavedPercentage}%
                </div>
                <div className="text-xs text-gray-600">Time Saved</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {formatDuration(flowData.transformation.timeSaved)}
                </div>
                <div className="text-xs text-gray-600">Reduction</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {flowData.transformation.stepsAutomated}
                </div>
                <div className="text-xs text-gray-600">Steps Automated</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-600">Generating process flow visualization...</span>
      </div>
    );
  }

  if (!flowData) {
    return (
      <div className="text-center py-12 text-gray-600">
        No process flow data available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitCompare className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-900">Process Flow Transformation</h2>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setView('before')}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                view === 'before'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Before
            </button>
            <button
              onClick={() => setView('comparison')}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                view === 'comparison'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Comparison
            </button>
            <button
              onClick={() => setView('after')}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                view === 'after'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              After
            </button>
          </div>

          {/* Export Buttons */}
          <button
            onClick={exportAsPNG}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            PNG
          </button>
          <button
            onClick={exportAsPDF}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Timeline Compression (shown in all views) */}
      {renderTimelineComparison()}

      {/* Process Flow Diagram */}
      <div ref={diagramRef} className="bg-white rounded-lg border-2 border-gray-200 p-6">
        {view === 'before' && (
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Current Process (Before Automation)</h3>
            {renderProcessFlow(flowData.before.steps, false)}

            {/* Before Metrics */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-900">{formatDuration(flowData.before.totalDuration)}</div>
                <div className="text-sm text-gray-600">Total Cycle Time</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-900">{flowData.before.totalHandoffs}</div>
                <div className="text-sm text-gray-600">Manual Handoffs</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-orange-600">{flowData.before.bottlenecks.length}</div>
                <div className="text-sm text-gray-600">Bottlenecks</div>
              </div>
            </div>
          </div>
        )}

        {view === 'after' && (
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Optimized Process (After Automation)</h3>
            {renderProcessFlow(flowData.after.steps, true)}

            {/* After Metrics */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="text-2xl font-bold text-green-600">{formatDuration(flowData.after.totalDuration)}</div>
                <div className="text-sm text-gray-600">Total Cycle Time</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="text-2xl font-bold text-green-600">{flowData.after.totalHandoffs}</div>
                <div className="text-sm text-gray-600">Manual Handoffs</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="text-2xl font-bold text-green-600">{flowData.transformation.stepsAutomated}</div>
                <div className="text-sm text-gray-600">Steps Automated</div>
              </div>
            </div>
          </div>
        )}

        {view === 'comparison' && (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Before: Current Process</h3>
              {renderProcessFlow(flowData.before.steps, false)}
            </div>

            <div className="flex items-center justify-center">
              <div className="bg-purple-100 rounded-full p-3">
                <ArrowRight className="w-8 h-8 text-purple-600" />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">After: Optimized Process</h3>
              {renderProcessFlow(flowData.after.steps, true)}
            </div>

            {/* Transformation Summary */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Transformation Impact</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-600">Time Saved</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {flowData.transformation.timeSavedPercentage}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDuration(flowData.transformation.timeSaved)}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-gray-600">Effort Reduced</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {flowData.transformation.effortReductionPercentage}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDuration(flowData.transformation.effortReduction)}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="text-sm text-gray-600">Steps Eliminated</span>
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    {flowData.transformation.stepsEliminated}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-gray-600">Handoffs Reduced</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    {flowData.transformation.handoffsReduced}
                  </div>
                </div>
              </div>
            </div>

            {/* Compliance Controls (if approved) */}
            {flowData.complianceControls && flowData.complianceControls.length > 0 && (
              <div className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-200 rounded-lg p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  Required Compliance Controls
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  These controls must be implemented as part of the automation to maintain compliance and risk management standards.
                </p>
                <div className="space-y-4">
                  {flowData.complianceControls.map((section, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                      <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        {section.type}
                      </h5>
                      <div className="space-y-2">
                        {section.controls.map((control, controlIdx) => (
                          <div key={controlIdx} className="flex items-start gap-3 text-sm">
                            <div className="mt-0.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">
                                {control.requirement || control.control}
                              </div>
                              {(control.owner || control.implementation) && (
                                <div className="text-gray-600 text-xs mt-1">
                                  {control.owner && <span className="mr-3">👤 {control.owner}</span>}
                                  {control.implementation && <span>⚙️ {control.implementation}</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessFlowComparison;
